import React, { useCallback, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NotificationBell } from "@/components/NotificationBell";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/constants";
import {
  LayoutDashboard,
  FileWarning,
  CheckSquare,
  Wrench,
  Car,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Sun,
  Moon,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "pelapor", "approver", "technician"], end: true },
  { to: "/reports", label: "Laporan Kerusakan", icon: FileWarning, roles: ["admin", "pelapor", "approver", "technician"] },
  { to: "/approval", label: "Persetujuan", icon: CheckSquare, roles: ["admin", "approver"] },
  { to: "/workshop", label: "Tindak Lanjut Bengkel", icon: Wrench, roles: ["admin", "technician", "approver"] },
  { to: "/vehicles", label: "Riwayat Kendaraan", icon: Car, roles: ["admin", "pelapor", "approver", "technician"] },
  { to: "/admin", label: "Master Data", icon: Settings, roles: ["admin"] },
];

export function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useIdleLogout(
    useCallback(async () => {
      await logout();
      toast.error("Sesi berakhir karena tidak ada aktivitas selama 10 menit.");
      navigate("/login");
    }, [logout, navigate]),
    10 * 60 * 1000
  );

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const SidebarInner = () => (
    <>
      <div className="flex items-center gap-3 px-5 py-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-sky-600 shadow-lg shadow-teal-500/20">
          <Wrench className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-display text-base font-extrabold leading-tight tracking-tight text-slate-900">
            SIPKD
          </p>
          <p className="text-[11px] leading-tight text-slate-500">Pelaporan Kerusakan</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1.5 px-3 py-2">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                isActive
                  ? "bg-[var(--sipkd-primary)] text-white shadow-lg shadow-teal-600/20"
                  : "text-slate-600 hover:bg-white hover:text-slate-900"
              }`
            }
          >
            <n.icon className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:scale-110" />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3">
        <div className="flex items-center gap-3 rounded-2xl bg-white px-3 py-3 shadow-sm">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-sky-600 text-xs font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-900">{user?.name}</p>
            <p className="truncate text-[11px] text-slate-500">
              {ROLE_LABELS[user?.role]} {user?.division_name ? `• ${user.division_name}` : ""}
            </p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          className="mt-2 flex w-full items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium text-slate-500 transition-colors duration-300 hover:bg-white hover:text-rose-600"
        >
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ background: "var(--sipkd-bg)" }}>
      {/* Desktop rail */}
      <aside
        className="fixed inset-y-0 left-0 hidden w-64 flex-col lg:flex"
        style={{ background: "var(--sipkd-rail)" }}
      >
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside
            className="absolute inset-y-0 left-0 flex w-64 flex-col"
            style={{ background: "var(--sipkd-rail)" }}
          >
            <button
              className="absolute right-3 top-5 rounded-full p-1.5 text-slate-500 hover:bg-white"
              onClick={() => setOpen(false)}
              data-testid="close-drawer-btn"
              aria-label="Tutup menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex w-full min-w-0 flex-1 flex-col lg:pl-64">
        {/* Floating pill topbar */}
        <header className="sticky top-0 z-30 px-4 pt-4 lg:px-8">
          <div className="glass flex h-14 items-center justify-between rounded-full border border-[var(--sipkd-border)] px-4 shadow-sm lg:px-6">
            <div className="flex items-center gap-2">
              <button
                className="rounded-full p-2 text-slate-600 transition-colors duration-300 hover:bg-slate-100 lg:hidden"
                onClick={() => setOpen(true)}
                data-testid="mobile-menu-btn"
                aria-label="Buka menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <span className="font-display text-sm font-semibold tracking-wide text-slate-500">
                Kendaraan Dinas
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="theme-toggle-btn"
                onClick={toggle}
                aria-label="Ganti tema"
                className="rounded-full p-2 text-slate-600 transition-colors duration-300 hover:bg-slate-100"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              {(user?.role === "pelapor" || user?.role === "admin") && (
                <Button
                  data-testid="header-new-report-btn"
                  size="sm"
                  className="hidden rounded-full bg-[var(--sipkd-primary)] px-4 text-white transition-colors duration-300 hover:bg-[var(--sipkd-primary-hover)] sm:flex"
                  onClick={() => navigate("/reports/new")}
                >
                  <Plus className="mr-1 h-4 w-4" /> Lapor Kerusakan
                </Button>
              )}
              <NotificationBell />
            </div>
          </div>
        </header>
        <main className="animate-rise w-full min-w-0 flex-1 overflow-x-hidden p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
